// Backend API Response Types (matching the actual API response)
export interface TaxDetail {
  Name: string
  Type: string
  JurisdictionName: string
  JurisdictionLevel: string
  JurisdictionCountryCode: string
  JurisdictionRegionCode: string
  TaxableAmount: number
  NonTaxableAmount: number
  TaxAmount: number
  TaxRate: number
}

export interface TaxItem {
  Id: string
  Sku: string
  Description?: string
  TaxCode: string
  TaxIncluded: boolean
  Quantity: number
  Amount: number
  TaxAmount: number
  Tax: TaxDetail[]
  ProviderId?: string
  ProviderTaxCategory?: string
  ProviderParentTaxCategory?: string
  FilingEntity?: string
}

export interface AddressWithId {
  Id: number
  Street: string
  City: string
  Region: string
  Country: string
  Postcode: string
}

export interface MerchantInfo {
  Id: string
  Name: string
}

export interface EngineDetail {
  Id: number
  EngineTransactionId: string
  EngineRefundId?: string | null
  MerchantSettlementCurrency: string
}

export interface AccountingInfo {
  EngineDetailId?: number
  TaxAccountingType: 'DEFAULT' | 'CHARGEBACK' | 'ORPHAN' | 'EZ_MORPH'
  EventState: string
  FilingToSettlementFallbackFxRate?: number
  EventTimestamp?: string
}

export interface ParentRelationship {
  ParentTransactionId: string
  ChildTransactionId: string
  Type: string
}

export interface ChildRelationship {
  ParentTransactionId: string
  ChildTransactionId: string
  Type: string
}

// Main TaxTransaction interface matching the API response
export interface TaxTransaction {
  Accounting?: AccountingInfo
  Amount: number
  BusinessIdentificationNumber?: string
  ChildRelationships?: ChildRelationship[]
  CreatedBy: string
  Reason: string
  CreatedDate: string
  Currency: string
  CustomerCode?: string
  EngineDetails: EngineDetail[]
  ExemptionNumber?: string
  FilingCurrency: string
  FilingExchangeRate: number
  FilingFxRateSource: string
  Id: string
  IsReversal: boolean
  Items?: TaxItem[]
  Merchant: MerchantInfo
  MerchantAddress?: AddressWithId
  ParentRelationships?: ParentRelationship[]
  Provider: string
  ProviderId: string
  ProviderReferenceId: string
  ProviderUrl?: string
  Reference: string
  ShippingAddress: AddressWithId
  TaxAmount: number
  TaxDate: string
  WarehouseAddress?: AddressWithId
}

export interface TaxTransactionSearchResponse {
  Content: TaxTransaction[]
  HasNext: boolean
  HasPrevious: boolean
  Size: number
  Number: number
  NumberOfElements: number
  First: boolean
  Last: boolean
  Empty: boolean
}

export interface TransactionSearchFilter {
  startDate?: Date | null
  endDate?: Date | null
  externalId?: string | null // Tax transaction ID
  referenceId?: string | null // Tax transaction reference ID
  providerId?: string | null // Provider ID
  providerReferenceId?: string | null // Provider reference ID
  engineTransactionId?: string | null // Engine transaction ID
  engineRefundId?: string | null // Engine refund ID
  chargebackId?: string | null // Chargeback ID
}

/**
 * Centralized filter type options for tax transaction search.
 * Used by search forms, dialogs, and tables.
 */
export const FILTER_TYPE_OPTIONS = [
  { label: 'ID', value: 'externalId' },
  { label: 'Reference ID', value: 'referenceId' },
  { label: 'Provider ID', value: 'providerId' },
  { label: 'Provider Reference ID', value: 'providerReferenceId' },
  { label: 'Engine Transaction ID', value: 'engineTransactionId' },
  { label: 'Engine Refund ID', value: 'engineRefundId' },
  { label: 'Chargeback ID', value: 'chargebackId' },
] as const

/**
 * Filter keys for iteration (excludes date fields).
 */
export const FILTER_KEYS = FILTER_TYPE_OPTIONS.map(o => o.value)
